import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { REJECT_CHANGE_REQUEST_MUTATION, ChangeRequestStatus, Node } from "../queries";
import Button from "@/components/Button";
import EmailPreviewDialog from "@/components/EmailPreviewDialog";
import Progress from "@/components/Progress";
import TextField from "@/components/TextField";
import useAmplitude from "@/hooks/useAmplitude";
import useModal from "@/hooks/useModal";
import { SECTION_ANALYTICS_EVENTS, getSectionAnalyticsData } from "@/pages/sections/_utils/amplitude";
import { useForm } from "react-hook-form";
import { useChangeRequestStatusChangeEmailPreview } from "../hooks/useChangeRequestStatusChangeEmailPreview";

type FormData = { message: string };

export default function RejectModal({
  changeRequest: { id: changeRequestId, change, section },
  onClosed
}: {
  changeRequest: Node;
  onClosed(): void;
}) {
  const { save, Modal, close } = useModal();
  const { trackEvent } = useAmplitude();
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useChangeRequestStatusChangeEmailPreview(changeRequestId, ChangeRequestStatus.REJECTED);

  useEffect(() => () => resetPreview(), [resetPreview]);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch
  } = useForm<FormData>({ defaultValues: { message: "" } });

  const [send, { loading }] = useMutation(REJECT_CHANGE_REQUEST_MUTATION);

  const submit = ({ message }: FormData) =>
    save(
      async () => {
        const result = await send({ variables: { id: changeRequestId, message } });
        if (result.data?.rejectChangeRequest?.success) {
          trackEvent(
            SECTION_ANALYTICS_EVENTS.changeRequestRejected,
            getSectionAnalyticsData({
              source: "section_change_requests",
              sectionId: section.id,
              changeRequestId,
              changeType: change.__typename,
              action: "reject",
              hasMessage: Boolean(message.trim())
            })
          );
        }
        return result;
      },
      (data) => data.rejectChangeRequest,
      "Change request rejected"
    );

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview(watch("message"));
  };

  return (
    <>
      <Modal
        className="w-[600px] max-w-[95vw]"
        actions={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <Button
              variant="text"
              icon="mail"
              onClick={handlePreviewEmail}
              disabled={loading}
              testId="preview-email-button"
            >
              Preview Email
            </Button>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="text" onClick={close} disabled={loading}>
                Cancel
              </Button>
              <Button theme="error" disabled={loading} onClick={handleSubmit(submit)} icon="chat">
                Send Rejection Message
              </Button>
            </div>
          </div>
        }
        headline="Reject Change Request"
        onClosed={onClosed}
      >
        {loading ? (
          <Progress variant="linear" />
        ) : (
          <>
            <p>Please include a message to the member specifying why this change request is being rejected.</p>

            <TextField
              className="w-full h-48 mt-6 mb-3"
              variant="outlined"
              type="textarea"
              label="Message to Member *"
              error={!!errors.message}
              errorText={errors.message?.message}
              value={watch("message")}
              {...register("message", { required: "Message is required" })}
            />
          </>
        )}
      </Modal>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
